<template>
  <div class="relative w-full h-full rounded-t-lg bg-light-box-background dark:bg-dark-box-background group/table">
    <div class="flex items-center justify-between" @dblclick="$emit('dbclick')">
      <div class="py-2 ml-2 mt-1 text-xl font-medium text-light-body-text dark:text-dark-body-text">
        {{ label }}
      </div>
      <div>
        <RotateCw
          v-if="!is_fetching_data"
          class="w-5 h-5 mt-2 mr-3 transition-opacity duration-300 rounded-full opacity-0 cursor-pointer group-hover:opacity-100 hover:bg-gray-200"
          aria-hidden="true"
          @click="getInitialRecords" />
        <LoadingSpinner v-else class="w-5 h-5 mt-2 mr-3" />
      </div>
    </div>
    <!-- Top Groups -->
    <div
      v-show="dataSource !== 'this_record' && dataSource !== 'another_record'"
      class="flex-row items-center justify-start w-full gap-2 px-1 space-y-1 sm:flex">
      <AdvancedMetricsFilter
        v-model:initial-filter="main_filter"
        v-model:selected-group="selected_group"
        :read-only="!!view_by"
        :current-filter="main_filter"
        :object-type="objectType"
        :disabled="!!view_by || !!appliedFilter"
        @cancel="
          () => {
            if (!view_by && !appliedFilter) {
              selected_group = null
              main_filter = appliedFilter ? appliedFilter : ''
            }
          }
        "
        @reset="
          ($event) => {
            selected_group = null
            main_filter = appliedFilter ? appliedFilter : ''
          }
        "
        @filter="
          ($event) => {
            main_filter = $utils.clone($event)
          }
        " />

      <SelectGroupDropDownMenu
        v-if="pageReference !== 'prospecting' && !view_by"
        v-model:current-item="selected_group"
        :items="
          $store.state.data?.groups.filter((group) => group.type == objectType && group.organization == $store.state?.organization?.id)
        "
        :object-type="objectType"
        :clear-selected-item="!selected_group ? true : false"
        label-attr="name"
        :placeholder="$t('Groups')"
        :is-box-background="true"
        class="relative w-[400px] bottom-1 py-[11px]"
        :input-classes="`sm:text-sm appearance-none h-10 font-medium text-gray-700 text-left block px-1 py-2 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light`" />

      <!-- Top Filters -->
      <div class="hidden xmd:flex w-full relative justify-between items-center h-[40px] pb-[5px] gap-0">
        <div v-if="selected_group?.description && !view_by" class="cursor-pointer has-tooltip">
          <Info class="w-8 h-8 text-common-grey" aria-hidden="true" />
          <span
            class="tooltip px-2 py-2 rounded-md text-sm max-w-[400px] bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">{{ selected_group.description }}</span>
        </div>

        <div class="flex items-center space-x-2">
          <div class="flex flex-shrink-0 -space-x-2">
            <div
              v-for="user in selected_users"
              :key="user"
              class="relative inline-block cursor-pointer group/delete has-tooltip"
              @click="removeUser(user)">
              <img
                v-if="$utils.getUserById(user, 'avatar')"
                onerror="this.onerror=null; this.src=window.default_avatar;"
                class="inline-block w-8 h-8 rounded-full hover:opacity-75"
                :src="$utils.getUserById(user, 'avatar')"
                alt="">
              <Avatar
                v-else
                :hover-effect="true"
                :first-name="$utils.getUserById(user, 'first_name')"
                :last-name="$utils.getUserById(user, 'last_name')"
                class="cursor-pointer hover:opacity-75" />

              <X
                class="absolute inset-0 self-center pt-1 pl-1 font-extrabold text-red-500 opacity-0 h-7 w-7 group-hover/delete:opacity-100"
                aria-hidden="true" />
              <span class="tooltip whitespace-nowrap">
                <p class="p-1 text-sm font-medium text-gray-700 bg-gray-300 rounded-sm w-fi group-hover/delete:text-gray-900">
                  {{ $utils.getUserById(user, "full_name") }}
                </p>
              </span>
            </div>
          </div>
        </div>

        <div v-click-outside="handleSearchBlur" class="w-full">
          <AutoCompleteInput
            :placeholder="$t('Search in') + ' ' + `${label}`"
            :object-type="objectType"
            :is-async="true"
            separator=","
            type="text"
            tabindex="0"
            classes="p-0 w-full ml-0"
            :input-classes="`form-input `"
            @changed="handleSearchTermChange"
            @chosen="
              ($event) => {
                handleSelectTerm($event)
              }
            "
            @reset="
              () => {
                search_term = ''
                search_term_queue = ''
              }
            " />
        </div>
        <div class="ml-1">
          <PeopleFilters
            v-model:owners="selected_owners"
            v-model:co-owners="selected_co_owners"
            v-model:team-members="selected_team_members" />
        </div>

        <div class="ml-1 has-tooltip">
          <RecordActionsDropDown
            v-if="hasButtons"
            :box-background="true"
            :disabled="editing_disabled"
            :component-reference="componentReference"
            :current-filter="full_filter"
            :icon-button="true"
            :object-type="objectType"
            :page-reference="pageReference"
            :selected-records="selected_records"
            :excluded-records="excluded_records"
            :is-select-all="is_select_all"
            :selected-full-records="selected_full_records"
            :count="initial_records.count" />
          <span v-if="editing_disabled"
                class="flex-wrap p-2 -ml-40 bg-gray-300 rounded tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
          </span>
        </div>
        <div
          v-if="hasButtons || (objectType == 'contact' && pageReference == 'company_profile' && $route.path.includes('company'))"
          class="ml-2">
          <button
            :disabled="editing_disabled"
            class="inline-flex px-2 py-2 font-medium border border-gray-200 rounded-md has-tooltip hover:bg-gray-100 sm:text-sm focus:outline-none"
            @click.passive="handleUpdateMetrics">
            <Grid2x2Plus class="w-5 h-5 text-gray-500" aria-hidden="true" />
            <span v-if="editing_disabled"
                  class="flex-wrap p-2 bg-gray-300 rounded -ml-52 mt-7 text- tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
            </span>
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="dataSource == 'this_record' || dataSource == 'another_record'"
      class="flex flex-row-reverse items-center w-full p-2 bg-white dark:bg-dark-box-background">
      <div class="ml-2">
        <button
          :disabled="editing_disabled"
          class="inline-flex px-2 py-2 font-medium border border-gray-200 rounded-md has-tooltip hover:bg-gray-100 sm:text-sm focus:outline-none"
          @click.passive="editing_disabled ? null : handleUpdateMetrics()">
          <Grid2x2Plus class="w-5 h-5 text-gray-500" aria-hidden="true" />
          <span v-if="editing_disabled"
                class="flex-wrap p-2 bg-gray-300 rounded -ml-52 mt-7 text- tooltip whitespace-nowrap">{{ $t("You don't have access to this feature") }}
          </span>
        </button>
      </div>
      <RecordActionsDropDown
        v-if="hasButtons"
        :box-background="true"
        :disabled="editing_disabled"
        :component-reference="componentReference"
        :current-filter="main_filter"
        :icon-button="true"
        :object-type="objectType"
        :page-reference="pageReference"
        :selected-records="selected_records"
        :excluded-records="excluded_records"
        :is-select-all="is_select_all"
        :selected-full-records="selected_full_records"
        :count="initial_records.count" />
      <div
        class="flex font-normal cursor-pointer items-center border-transparent bg-[#0D8ABA] p-[8px] text-white dark:bg-dark-brand-blue rounded border-solid border-[1px] w-fit content-center text-[14px]"
        @click="handleAddNewRecord">
        <Plus class="w-5 h-5 ml-1" /> {{ $t("Add") }} {{ $utils.ObjectType(objectType).label }}
      </div>
    </div>
    <div class="relative w-full h-[calc(100%-120px)] overflow-visible">
      <transition-group tag="div" mode="in-out" name="slide-panel" :class="[`overflow-visible  h-[calc(100%)]`]">
        <div
          :key="componentReference"
          class="w-full h-full max-w-full max-h-full min-w-full min-h-full overflow-visible slide-panel-item bg-light-box-background dark:bg-dark-box-background">
          <AdvancedCompaniesMetricsTable
            :key="componentReference"
            v-model:is-select-all="is_select_all"
            v-model:selected-records="selected_records"
            v-model:excluded-records="excluded_records"
            v-model:selected-full-records="selected_full_records"
            v-model:records="records"
            v-model:filter="main_filter"
            v-model:columns-filter="columns_filter"
            v-model:selected-users="selected_users"
            v-model:initial-records="initial_records"
            v-model:sorting="sorting"
            v-model:page-size="page_size"
            v-model:is-loading="is_fetching_data"
            :edit-table-trigger="edit_table_trigger"
            :full-filter="full_filter"
            :selected-group="selected_group"
            :object-type="objectType || $route.params?.page"
            :page-reference="pageReference"
            :component-reference="componentReference"
            :is-searching="is_searching"
            :adjustable="true" />
        </div>
      </transition-group>
    </div>

    <div
      v-if="bottom_menu_items.length"
      class="absolute bottom-0 flex justify-center w-full transition-all duration-500 ease-in-out opacity-0 pointer-events-none group-hover/table:opacity-100 group-hover/table:bottom-6">
      <div class="flex items-center justify-between h-16 px-3 space-x-8 bg-gray-100 rounded-full shadow-md pointer-events-auto w-fit">
        <div
          v-for="(item, index) in bottom_menu_items"
          :key="index"
          class="transition-all duration-500 ease-in-out cursor-pointer hover:text-black text-common-grey"
          @click="item.action">
          <component :is="item.icon" class="inline mr-2" /> {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdvancedCompaniesMetricsTable from "@/components/shared/AdvancedCompaniesMetricsTable.vue"
import AdvancedMetricsFilter from "@/components/shared/AdvancedMetricsFilter.vue"
import RecordActionsDropDown from "@/components/shared/RecordActionsDropDown.vue"
import { Save, CopyX, Grid2x2Plus, X, Plus, RotateCw, Info } from "lucide-vue-next"
import dataService from "@/services/data.service"
import SelectGroupDropDownMenu from "@/components/shared/SelectGroupDropDownMenu.vue"
import { VIEW_TYPES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import Avatar from "@/components/shared/Avatar.vue"
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue"
import AutoCompleteInput from "@/components/shared/AutoCompleteInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import PeopleFilters from "./PeopleFilters"

export default {
  components: {
    CopyX,
    Save,
    SelectGroupDropDownMenu,
    LoadingSpinner,
    X,
    RotateCw,
    AutoCompleteInput,
    Info,
    Avatar,
    Grid2x2Plus,
    AdvancedMetricsFilter,
    AdvancedCompaniesMetricsTable,
    RecordActionsDropDown,
    Plus,
    PeopleFilters
  },

  inject: ["canUserEditLayout"],
  props: {
    dontLoad: {
      type: Boolean,
      default: false
    },
    appliedFilter: {
      type: String,
      required: false
    },
    pageReference: {
      type: String,
      required: true
    },
    //current template data sources
    dataSources: {
      type: Object,
      required: false
    },
    objectType: {
      type: String,
      required: true
    },
    componentReference: {
      type: String,
      required: true
    },
    hasButtons: {
      default: true
    },
    label: {
      type: String,
      default: ""
    },
    dataSource: {
      type: String,
      default: ""
    },
    linkedRecord: {
      type: String,
      required: false
    },
    view_by: {
      type: String,
      default: null
    },

    preservedMainFilter: null,
    preservedColumnsFilters: null,
    preservedSelectedTeamMembers: null,
    preservedSelectedOwners: null,
    preservedSelectedCoOwners: null,
    preservedPageSize: {
      type: Number,
      default: 20
    }
  },
  emits: [
    "update:openEditMetrics",
    "update:preservedFilters",
    "selectedRecords",
    "selectedFullRecords",
    "currentFilter",
    "selectedGroup",
    "selectedOwners",
    "dbclick",
    "pulselclick"
  ],

  data() {
    return {
      parent_record_uuid: null,
      page_size: 20,
      is_searching: false,

      selected_group: null,
      selected_users: [],
      selected_user: null,
      // smart table outputs
      records: [],

      initial_records: [],
      selected_records: [],
      excluded_records: [],
      selected_full_records: [],
      sorting: "",
      is_fetching_data: false,
      avoid_group_trigger: false,
      avoid_blur_trigger: false,

      main_filter: "",
      columns_filter: "",
      search_term: "",
      selected_owners: [],
      selected_co_owners: [],
      selected_team_members: [],
      search_term_queue: "",
      edit_table_trigger: 1,
      edit_table_active: false,
      is_select_all: false,
      prevent_watch: false
    }
  },
  computed: {
    parentRecord() {
      if (this.dataSource == "another_record") {
        return this.linkedRecord
      } else if (this.dataSource == "this_record") {
        return this.$route.params.cid
      }
      return null
    },

    bottom_menu_items() {
      const all_items = [
        {
          name: this.$t("Clear all filters"),
          icon: "CopyX",
          action: () => this.clearAllFilters(),
          visible: this.clearAllFiltersVisible()
        },
        {
          name: this.$t("Save filter") + ` ${this.selected_group ? this.$t("to") : this.$t("as")}` + " " + this.$t("group"),
          icon: "Save",
          action: () => this.saveFilterToGroup(),
          visible: this.saveFilterToGroupVisible()
        },
        {
          name: this.$t("Save edits"),
          icon: "Save",
          action: () => this.saveTableEdits(),
          visible: this.edit_table_active
        }
      ]
      return all_items.filter((item) => item.visible)
    },
    main_and_column_filters() {
      let full_filter_calc = ""
      if (this.main_filter) {
        full_filter_calc = this.main_filter
      }
      if (this.columns_filter) {
        full_filter_calc = full_filter_calc ? `(${full_filter_calc}) and (${this.columns_filter})` : this.columns_filter
      }

      return full_filter_calc
    },

    full_filter() {
      let full_filter_calc = ""
      if (this.main_filter) {
        full_filter_calc = this.main_filter
      }
      if (this.columns_filter) {
        full_filter_calc = full_filter_calc ? `(${full_filter_calc}) and (${this.columns_filter})` : this.columns_filter
      }
      if (this.search_term) {
        let name_filter
        if (this.objectType == "contact") {
          name_filter = [
            this.$utils.getOptionMetricReference("contact", "first_name"),
            "in",
            `'${this.search_term.split(" ")[0]}'`,
            this.search_term.split(" ").length > 1 && this.search_term.split(" ")[1] ? "and" : "or",
            this.$utils.getOptionMetricReference("contact", "last_name"),
            "in",
            `'${
              this.search_term.split(" ").length > 1 && this.search_term.split(" ")[1] ? this.search_term.split(" ")[1] : this.search_term
            }'`,
            "or",
            this.$utils.getOptionMetricReference("contact", "email"),
            "in",
            `'${this.search_term.split(" ")[0]}'`
          ].join(" ")
        } else {
          name_filter = `${this.$utils.getOptionMetricReference(this.objectType, "name")} in '${this.search_term}'`
        }
        full_filter_calc = full_filter_calc ? `((${full_filter_calc}) and (${name_filter}))` : name_filter
      }

      if (this.selected_owners.length) {
        const owner_filter_string_calc = `( owner__id in [${this.selected_owners.map((o) => `'${o}'`).join(", ")}] )`
        full_filter_calc = full_filter_calc ? `${full_filter_calc} and ${owner_filter_string_calc}` : owner_filter_string_calc
      }

      if (this.selected_co_owners.length) {
        let co_owner_filter_string_calc = `( co_owners__id in [${this.selected_co_owners.map((o) => `'${o}'`).join(", ")}] )`
        full_filter_calc = full_filter_calc ? `${full_filter_calc} and ${co_owner_filter_string_calc}` : co_owner_filter_string_calc
      }

      if (this.selected_team_members.length) {
        let team_members_filter_string_calc = `( owner__id in [${this.selected_team_members.map((o) => `'${o}'`).join(", ")}] )`
        full_filter_calc = full_filter_calc ? `${full_filter_calc} and ${team_members_filter_string_calc}` : team_members_filter_string_calc
      }

      if (this.parentRecord) {
        if (full_filter_calc) {
          full_filter_calc = full_filter_calc + ` and ( parent == '${this.parentRecord}' )`
        } else {
          full_filter_calc = `( parent == '${this.parentRecord}' )`
        }
      }

      return full_filter_calc
    },

    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },

  watch: {
    page_size() {
      this.getInitialRecords(2)
      this.updatePreservedFilters()
    },
    main_filter() {
      if (this.selected_group && this.selected_group.filter !== this.main_filter) {
        this.selected_group = null
      }
      this.updatePreservedFilters()
    },
    columns_filter() {
      this.updatePreservedFilters()
    },
    selected_team_members() {
      this.updatePreservedFilters()
      this.getInitialRecords(1)
    },
    selected_owners() {
      this.updatePreservedFilters()
    },
    selected_co_owners() {
      this.updatePreservedFilters()
    },
    parentRecord() {
      this.getInitialRecords(2)
    },

    selected_user() {
      if (this.selected_user) {
        this.selected_users.push(this.selected_user)
        this.selected_users = utils.clone(this.selected_users)
      }
      this.getInitialRecords(3)
    },
    selected_users() {
      this.$emit("selectedOwners", this.selected_users) // for PageView RollupMetricsGrid and GraphsGrid
      this.$emitter.emit(this.componentReference, {
        event: "selectedOwners",
        data: this.selected_users
      }) // for PageView RollupMetricsGrid and GraphsGrid
    },
    appliedFilter() {
      this.main_filter = this.appliedFilter ? this.appliedFilter : ""
    },
    full_filter() {
      this.$emitter.emit(this.componentReference, {
        event: "currentFilter",
        data: this.full_filter
      })

      if (!this.avoid_group_trigger) {
        this.getInitialRecords(4)
      }
    },
    view_by() {
      this.selected_group = this.$store.state.data?.groups.find((group) => group.id == this.view_by)
      this.main_filter = this.selected_group?.filter
    },
    selected_group() {
      this.$emit("selectedGroup", this.selected_group) // for PageView RollupMetricsGrid and GraphsGrid
      if (!this.avoid_group_trigger) {
        this.$emitter.emit(this.componentReference, {
          event: "selectedGroup",
          data: this.selected_group
        })
      }
      if (this.selected_group) {
        this.main_filter = this.appliedFilter ? `${this.appliedFilter} and (${this.selected_group?.filter})` : this.selected_group?.filter
      } else {
        if (!this.main_filter) {
          this.main_filter = this.appliedFilter ? this.appliedFilter : ""
        } else {
          this.main_filter = ""
        }
      }

      if (
        this.$store.state.ui[`${this.pageReference}>${this.componentReference}>${this.objectType}__selected_group`] !==
        this.selected_group?.id
      ) {
        this.$store.dispatch("ui/setUIValue", {
          [`${this.pageReference}>${this.componentReference}>${this.objectType}__selected_group`]: this.selected_group?.id
        })
      }
    },
    sorting() {
      this.getInitialRecords(5)
    },
    selected_records() {
      this.$emit("selectedRecords", this.selected_records) // for PageView RollupMetricsGrid and GraphsGrid
      this.$emitter.emit(this.componentReference, {
        event: "selectedRecords",
        data: this.selected_records
      })
    },

    excluded_records: {
      deep: true,
      handler() {
        this.$emitter.emit(this.componentReference, {
          event: "excludedRecords",
          data: this.excluded_records
        })
      }
    },
    selected_full_records() {
      this.$emit("selectedFullRecords", this.selected_full_records) // for assign owner/co-owners
      // this.$emitter.emit(this.componentReference, { event: "selectedRecords", data: this.selected_full_records.map((x) => x.id) })
    }
  },
  activated() {
    this.register_listeners()
    this.getInitialRecords(6)
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  created() {
    this.register_listeners()

    if (this.appliedFilter) {
      this.main_filter = this.appliedFilter
    }
    if (this.view_by) {
      this.avoid_group_trigger = true
      setTimeout(() => {
        this.avoid_group_trigger = false
      }, 1000)
      this.selected_group = this.$store.state.data?.groups.find((group) => group.id == this.view_by)
      this.main_filter = this.selected_group?.filter
    }

    if (!this.view_by) {
      this.avoid_group_trigger = true
      setTimeout(() => {
        this.avoid_group_trigger = false
      }, 1000)
      let preselected_group_id =
        this.$store.state.ui_options[`${this.pageReference}>${this.componentReference}>${this.objectType}__selected_group`]
      if (typeof preselected_group_id === "string") {
        this.selected_group = this.$store.state.data?.groups.find((group) => group.id == preselected_group_id)
        if (this.selected_group) {
          this.main_filter = this.selected_group?.filter
        }
      }
    }

    this.initPreservedFilters()
    this.getInitialRecords(7)
  },

  methods: {
    updatePreservedFilters() {
      if (!this.prevent_watch) {
        this.$emit("update:preservedFilters", {
          preservedMainFilter: this.main_filter,
          preservedColumnsFilters: this.columns_filter,
          preservedSelectedTeamMembers: this.selected_team_members,
          preservedSelectedOwners: this.selected_owners,
          preservedSelectedCoOwners: this.selected_co_owners,
          preservedPageSize: this.page_size
        })
      }
    },
    initPreservedFilters() {
      this.prevent_watch = true
      if (this.preservedMainFilter) {
        this.main_filter = this.preservedMainFilter
      }

      if (this.preservedColumnsFilters) {
        this.columns_filter = this.preservedColumnsFilters
      }

      if (this.preservedSelectedTeamMembers) {
        this.selected_team_members = this.preservedSelectedTeamMembers
      }

      if (this.preservedSelectedOwners) {
        this.selected_owners = this.preservedSelectedOwners
      }

      if (this.preservedSelectedCoOwners) {
        this.selected_co_owners = this.preservedSelectedCoOwners
      }

      if (this.preservedPageSize) {
        this.page_size = this.preservedPageSize > 50 ? 50 : this.preservedPageSize
      }
      setTimeout(() => {
        this.prevent_watch = false
      })
    },

    handleSelectTerm(e) {
      this.avoid_blur_trigger = true
      setTimeout(() => {
        this.avoid_blur_trigger = false
      }, 1000)
      this.search_term_queue = e
      this.search_term = e
    },
    clearAllFiltersVisible() {
      let clear_all_filters_visible = false
      if (this.selected_group) {
        if (this.selected_group.filter !== this.main_filter) {
          clear_all_filters_visible = true
        }
      } else if (!this.appliedFilter) {
        clear_all_filters_visible = this.main_filter
      }

      if (this.columns_filter || this.selected_owners.length || this.selected_co_owners.length || this.selected_team_members.length) {
        clear_all_filters_visible = true
      }

      return clear_all_filters_visible
    },
    saveFilterToGroupVisible() {
      let save_filter_to_group_visible = false
      if (this.selected_group) {
        if (this.selected_group.filter !== this.main_filter) {
          save_filter_to_group_visible = true
        }
      } else {
        save_filter_to_group_visible = this.main_filter
      }
      if (this.columns_filter) {
        save_filter_to_group_visible = true
      }

      return save_filter_to_group_visible
    },
    clearAllFilters() {
      // main_filter: "",
      // columns_filter: "",
      // search_term: "",
      // selected_owners: [],
      // selected_co_owners: [],
      // selected_team_members: [],
      // search_term_queue: ""
      if (this.selected_group) {
        this.main_filter = this.selected_group?.filter
      } else if (!this.appliedFilter) {
        this.main_filter = ""
      }

      this.columns_filter = ""
      this.selected_owners = []
      this.selected_co_owners = []
      this.selected_team_members = []
      this.$toast.open({
        message: `All filters cleared!`,
        position: "top",
        type: "success",
        duration: 3000
      })
    },
    saveFilterToGroup() {
      if (this.selected_group) {
        const new_filter = this.main_and_column_filters
        dataService
          .updateGroup(this.selected_group.id, {
            ...this.selected_group,
            filter: new_filter
          })
          .then((res) => {
            this.selected_group = res.data
            this.columns_filter = ""
            this.$toast.open({
              message: `Group filter updated!`,
              position: "top",
              type: "success",
              duration: 3000
            })
          })
      } else {
        this.$emitter.emit(ALL_EMITTERS.CREATE_GROUP, {
          filter: this.main_and_column_filters,
          object_type: this.objectType
        })
      }
    },

    saveTableEdits() {
      this.edit_table_trigger -= 1
      this.edit_table_active = false
    },
    handleSearchBlur() {
      if (!this.avoid_blur_trigger) {
        this.search_term = this.search_term_queue
      }
    },
    handleSearchTermChange(e) {
      this.search_term_queue = e
    },

    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.GROUP_UPDATED_IN_OBJECT_VIEW, (group) => {
        this.selected_group = group
      })

      this.$emitter.on(ALL_EMITTERS.NEW_GROUP_CREATED_IN_OBJECT_VIEW, (group) => {
        this.selected_group = group
        this.columns_filter = ""
      })
      this.$emitter.on(ALL_EMITTERS.SUCCESSFULLY_DELETED, (group_id) => {
        if (group_id == this.selected_group.id) {
          this.selected_group = null
        }
      })

      this.$emitter.on(ALL_EMITTERS.METRICS_UPDATED_IN_OBJECT_VIEW, () => {
        this.getInitialRecords(true)
      })

      // TODO: Djelloul Add some description for this emitter when its called
      this.$emitter.on(ALL_EMITTERS.REFRESH_TABLE, () => {
        this.getInitialRecords(8)
      })

      this.$emitter.on(ALL_EMITTERS.BULK_DELETED, (records) => {
        this.records = this.records.filter((record) => !records.filter((r) => r == record.id).length)

        this.initial_records = {
          ...this.initial_records,
          results: this.initial_records.results.filter((record) => !records.filter((r) => r == record.id).length),
          count: this.initial_records.count - records.length
        }
        this.selected_records = []
        this.selected_full_records = []
      })
      //UPDATE_BULK_DELETE_ACTION_STATUS on this refresh the count
      this.$emitter.on(ALL_EMITTERS.UPDATE_BULK_DELETE_ACTION_STATUS, (data) => {
        let object_type = data.object_type
        let deleted_count = data.deleted_count
        if (object_type == this.objectType) {
          this.initial_records.count = this.initial_records.count - deleted_count
        }
      })
      // this.$emitter.on(ALL_EMITTERS.RESET_INITIAL_RECORDS, ({ result: result, component_reference: component_reference }) => {
      //   if (component_reference == this.componentReference) {
      //     this.records = result.data.results

      //     console.log(555)

      //     this.initial_records = result.data
      //     this.is_fetching_data = false
      //   }
      // })
      this.$emitter.on(ALL_EMITTERS.SELECTED_USERS, (users) => {
        this.selected_users = utils.clone(users)
        this.getInitialRecords(9)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.NEW_GROUP_CREATED_IN_OBJECT_VIEW)
      this.$emitter.off(ALL_EMITTERS.SUCCESSFULLY_DELETED)
      this.$emitter.off(ALL_EMITTERS.METRICS_UPDATED_IN_OBJECT_VIEW)
      this.$emitter.off(ALL_EMITTERS.REFRESH_TABLE)
      this.$emitter.off(ALL_EMITTERS.BULK_DELETED)
      this.$emitter.off(ALL_EMITTERS.RESET_INITIAL_RECORDS)
      this.$emitter.off(ALL_EMITTERS.SELECTED_USERS)
      this.$emitter.off(ALL_EMITTERS.UPDATE_BULK_DELETE_ACTION_STATUS)
    },
    handleUpdateMetrics() {
      const metrics_obj = {
        page_reference: this.pageReference,
        object_type: this.objectType || this.$route.params?.page,
        view_type: VIEW_TYPES.TABLE
      }
      if (this.$route.params?.page) {
        metrics_obj["layout_page_reference"] = this.$route.params?.page
      }
      if (this.componentReference) {
        metrics_obj["component_reference"] = this.componentReference
      }

      this.$emitter.emit(ALL_EMITTERS.UPDATE_METRICS, metrics_obj)
    },

    handleAddNewRecord() {
      const metrics_obj = {
        object_type: this.objectType,
        page_reference: this.pageReference,
        view_type: VIEW_TYPES.FORM_CREATE
      }
      if (this.$route.params?.page) {
        metrics_obj["layout_page_reference"] = this.$route.params?.page
      }
      if (this.componentReference) {
        metrics_obj["component_reference"] = this.componentReference
      }

      this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
    },
    removeUser(user) {
      let index = this.selected_users.findIndex((u) => u == user)
      this.selected_users.splice(index, 1)
      if (this.selected_users.length == 0) {
        this.selected_user = null
      }
      this.selected_users = utils.clone(this.selected_users)
      this.getInitialRecords(10)
    },
    updateSameInitialDataSourcesCallsTable(result) {
      /* Checks if the any of the data sources included in the current template has included this records table as same_initial_calls_data_sources
        happens when two or more data sources are fetching the same data and we want to avoid fetching the same data twice initially
      */
      let current_records_table_data_source = Object.values(this.dataSources).find(
        (data_source) =>
          data_source.component_reference == this.componentReference && data_source.same_initial_calls_data_sources.length > 0
      )
      if (current_records_table_data_source && !this.parentRecord) {
        current_records_table_data_source.same_initial_calls_data_sources.forEach((component_reference) => {
          this.$emitter.emit(ALL_EMITTERS.RESET_INITIAL_RECORDS, {
            result: result,
            component_reference: component_reference
          })
        })
      }
    },
    getInitialRecords(avoidLoading) {
      if (!avoidLoading && !this.dontLoad) {
        this.$store.dispatch("ui/showLoading")
      }
      if (!this.is_fetching_data) {
        let page_size_temp_calc = this.page_size
        if (page_size_temp_calc > 50) {
          page_size_temp_calc = 50
        }
        this.is_fetching_data = true
        let params = {
          type: this.objectType,
          page_size: page_size_temp_calc,
          ordering: this.sorting
        }

        if (utils.isNotEmpty(this.full_filter) || utils.isNotEmpty(this.selected_team_members)) {
          let full_filter_calc = this.full_filter
          this.parent_record_uuid = this.parentRecord
          if (this.parentRecord) {
            // if the this.parentRecord is not uuid then we get the parentRecord first
            if (!utils.isUUID(this.parentRecord)) {
              dataService.getRecord(this.parentRecord).then((result) => {
                this.parent_record_uuid = result.data.id
                let correct_path_with_uuid = this.$route.path.replace(this.parentRecord, this.parent_record_uuid)
                this.$router.replace(correct_path_with_uuid)
              })
            }
          }
          dataService
            .filterRecords(
              {
                filter: full_filter_calc,
                object_types: [this.objectType],
                page_size: page_size_temp_calc,
                owners: this.selected_team_members
              },
              params
            )
            .then((result) => {
              this.records = result.data.results
              this.initial_records = { ...result.data, results: this.records }
              this.is_fetching_data = false
              this.updateSameInitialDataSourcesCallsTable(result)
              this.$store.dispatch("ui/hideLoading")
            })
            .catch(() => {
              this.is_fetching_data = false
              this.$store.dispatch("ui/hideLoading")
            })
        } else {
          dataService
            .getRecords(params)
            .then((result) => {
              this.records = result.data.results
              this.initial_records = { ...result.data, results: this.records }
              this.is_fetching_data = false
              this.updateSameInitialDataSourcesCallsTable(result)
              this.$store.dispatch("ui/hideLoading")
            })
            .catch(() => {
              this.is_fetching_data = false
              this.$store.dispatch("ui/hideLoading")
            })
        }
      }
    }
  }
}
</script>

<style>
.slide-panel-item {
  transition: all 0.4s ease;
  display: block;
}

.slide-panel-leave-to {
  transition-duration: 800ms;
  transform: translateY(-100%);
}

.slide-panel-enter-from {
  transition-duration: 400ms;
  transform: translateY(0);
}

.slide-panel-leave-active {
  position: absolute;
  width: 100%;
}
.text-shadow :hover {
  text-shadow: 0px 0px 1px black;
}
</style>
